import {
	AsyncSelect,
	AsyncSelectProps,
	Badge,
	Box,
	Dropdown,
	Icons,
	Inline,
	joinAttributes,
	Money,
	RelativeTime,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import { isTruthy, SwitchInputField } from "@sembark-travel/ui-form"
import {
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { ButtonLink, useLocationQuery } from "@sembark-travel/ui-router"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import { Fragment, useEffect } from "react"
import { generatePath } from "../router-utils"
import { TCustomerPaymentPreference } from "./store"
import { formatMoneyByDecimal } from "@sembark-travel/money"

type TFilters = TSearchParams & {
	q?: string
	disabled_only?: boolean
}

type TQueryParams = TSearchParams & {
	q?: string
	disabled_only?: boolean
}
function queryParamsToFilters(params: TQueryParams): TFilters {
	const { q, disabled_only } = params
	const filters: TFilters = {}
	if (q) {
		filters.q = q
	}
	if (disabled_only) {
		filters.disabled_only = disabled_only
	}

	return filters
}

function filtersToQueryParams(filters: TFilters): TQueryParams {
	const { q, disabled_only } = filters
	const query: TQueryParams = {}
	if (q) {
		query.q = q
	}
	if (disabled_only) {
		query.disabled_only = disabled_only
	}

	return query
}
function filtersToRequest(params: TFilters) {
	const { q, disabled_only, ...otherParams } = params
	return {
		...otherParams,
		q: q,
		disabled_only: disabled_only ? 1 : undefined,
	}
}

export function CustomerPaymentPreferenceList() {
	const xhr = useXHR()
	const [query, setQuery] = useLocationQuery<TQueryParams, TFilters>({
		toQuery: filtersToQueryParams,
		fromQuery: queryParamsToFilters,
	})
	const [params, setParams] = useSearch<TFilters>({
		...query,
	})

	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])

	return (
		<Fragment>
			<Search
				title="Customer Payment Preferences"
				Filters={Filters}
				initialParams={params}
				onSearch={(params) => setParams({ ...params, page: 1 })}
				areAdvancedFiltersApplied={(params) => {
					const { disabled_only } = params
					return isTruthy(disabled_only)
				}}
				actions={
					<ButtonLink to={generatePath("/customer-payment-preferences/new")}>
						Add New
					</ButtonLink>
				}
			>
				<ListView<TCustomerPaymentPreference>
					pageKey="customer-payment-preferences"
					fetch={async (xhr, params) => {
						const filterParams = filtersToRequest(params)
						return xhr
							.get("/customer-payment-preferences", {
								params: { ...filterParams, include: "trip_count,created_by" },
							})
							.then((resp) => {
								return resp.data
							})
					}}
					onPageChange={(page) => setParams({ ...params, page })}
					params={params}
				>
					{({ items, refresh }) => (
						<Table
							bordered
							responsive
							headers={["Description", "Used In", "Created by", ""]}
							rows={items.map((item) => [
								<Stack key={item.id}>
									<Inline gap="1" alignItems={"center"}>
										<Text fontSize={"base"} fontWeight="normal">
											{item.name}
										</Text>
										<Box>
											{item.deleted_at ? <Icons.Ban color={"danger"} /> : null}
										</Box>
									</Inline>
									<Text fontSize={"base"} color={"muted"}>
										{item.preference_condition}
									</Text>
									<Inline gap={"2"}>
										{item.flight_include ? (
											<Badge success>Flight included</Badge>
										) : null}

										<FirstInstalmentBadge
											value={item.min_first_instalment_amounts}
										/>
									</Inline>
								</Stack>,
								<Box>{item.trips_count ?? 0}</Box>,
								<Text>
									<Text textTransform="capitalize">
										{item.created_by?.name || "You"}{" "}
									</Text>{" "}
									<RelativeTime
										timestamp={item.created_at}
										color="muted"
										fontSize="sm"
									/>
								</Text>,
								<Dropdown alignRight="sm">
									<Dropdown.ToggleButton>
										<Icons.DotsVertical />
									</Dropdown.ToggleButton>
									<Dropdown.Menu>
										{!item.deleted_at ? (
											<Dropdown.MenuItem
												onClick={async () => {
													if (
														window.confirm(
															"Are you sure you want to disable this customer payment preference ? You can restore it any time."
														)
													) {
														try {
															const res = await xhr.delete(
																`/customer-payment-preferences/${item.id}`
															)
															const { message } = res.data.data
															refresh()
															showSnackbar(
																message ||
																	"Customer payment preference disabled successfully"
															)
														} catch (err) {
															const error = err as Error
															showSnackbar(
																error.message ||
																	"Failed to disable customer payment preference"
															)
														}
													}
												}}
											>
												<Icons.Ban color="danger" /> Disable
											</Dropdown.MenuItem>
										) : (
											<Dropdown.MenuItem
												onClick={async () => {
													if (
														window.confirm(
															"Are you sure you want to restore this disabled customer payment preference ?"
														)
													) {
														try {
															const res = await xhr.post(
																`/customer-payment-preferences/${item.id}/restore`
															)

															const { message } = res.data.data

															refresh()
															showSnackbar(
																message ||
																	"Customer payment preference restored successfully"
															)
														} catch (err) {
															const error = err as Error
															showSnackbar(
																error.message ||
																	"Failed to restore customer payment preference"
															)
														}
													}
												}}
											>
												<Icons.Refresh color="success" /> Restore
											</Dropdown.MenuItem>
										)}
									</Dropdown.Menu>
								</Dropdown>,
							])}
						/>
					)}
				</ListView>
			</Search>
		</Fragment>
	)
}

export function SelectCustomerPaymentReferences({
	...otherProps
}: Omit<AsyncSelectProps, "fetch">) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			multiple
			{...otherProps}
			fetch={(q) =>
				xhr
					.get("/customer-payment-preferences/references", { params: { q } })
					.then((resp) => resp.data.data)
			}
		/>
	)
}

function Filters() {
	return <SwitchInputField name="disabled_only" label="Disabled Only" />
}

function FirstInstalmentBadge({
	value,
}: {
	value: TCustomerPaymentPreference["min_first_instalment_amounts"]
}) {
	return value?.length === 0 ? null : (
		<Box>
			<Badge warning>
				<Inline gap={"2"}>
					Minimum Amount:
					<Inline gap={"px"}>
						{joinAttributes(
							...value.map(({ amount }, index) => (
								<Money
									key={index}
									amount={Number(formatMoneyByDecimal(amount))}
									currency={amount.currency}
									showCurrency
								/>
							))
						)}
					</Inline>
				</Inline>
			</Badge>
		</Box>
	)
}
